import { Dropdown, Field, IMessageProps, Link, TextAreaInput, TextInput, TextOutput } from 'Components/Core';
import { FieldSet, ListFieldSection, PageSection, SectionGroup } from 'Components/Layout';
import { ListDropdown } from 'Components/List';
import { actions } from 'Data/ReduxViewModels/Admin';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IReportConfiguration, reportConfigurationTypeOptions } from './types';

const ReportConfigurationAdmin = () => {
  const dispatch = useDispatch();
  const data: IReportConfiguration = useSelector((state: any) => state.admin.syncModel);
  const [nameFieldMessages, setNameFieldMessages] = useState<any>([]);

  const nameFieldOnChange = (evt: any) => {
    let submit: boolean = evt.data.submitValues;

    if (!evt.data.value || evt.data.value.length === 0) {
      submit = false;
      setNameFieldMessages([{ severity: 'error', message: 'Name cannot be empty' }]);
    } else {
      setNameFieldMessages([]);
    }

    dispatch(actions.setValue({ name: evt.data.name, value: evt.data.value, sync: submit }));
  };

  const baseConfigurationUUIDMessages: IMessageProps[] | undefined = data.Messages?.filter(
    (m: any) => m.PropertyName === 'BaseConfiguration'
  ).map((m: any) => ({
    message: m.Text,
    severity: m.MessageType.toLowerCase(),
  }));

  const contentMessages: IMessageProps[] | undefined = data.Messages?.filter(
    (m: any) => m.PropertyName === 'Content'
  ).map((m: any) => ({
    message: m.Text,
    severity: m.MessageType.toLowerCase(),
  }));
  return (
    <>
      <PageSection heading="Details">
        <SectionGroup>
          <ListFieldSection wide>
            <Field
              input={<TextInput />}
              label="Name"
              name="syncModel.Name"
              maxLength={100}
              readOnly={data.IsSystemConfiguration}
              onChange={nameFieldOnChange}
              messages={nameFieldMessages}
            />
            <Field
              label="Configuration Type"
              input={<Dropdown />}
              options={reportConfigurationTypeOptions}
              name="syncModel.ConfigurationType"
              disabled={data.IsSystemConfiguration || Boolean(data.BaseConfigurationUUID)}
              simpleValue
            />
            <FieldSet>
              <Field
                input={<ListDropdown />}
                label="Modifies Configuration"
                disabled={data.IsSystemConfiguration}
                name="syncModel.BaseConfigurationUUID"
                queryParams={{ ConfigType: data.ConfigurationType, IncludeSystem: true }}
                value={
                  data.BaseConfigurationUUID
                    ? {
                        value: data.BaseConfigurationUUID,
                        label: data.BaseConfigurationName ?? ' ',
                      }
                    : null
                }
                endpoint="list.reportConfigurations"
                disabledOptions={[data.Id]}
                additionalInformationDialogContent={
                  <>
                    <p>Select another configuration that this configuration will be applied to as a modification.</p>
                    <p>Leave blank for new complete configurations.</p>
                  </>
                }
                messages={baseConfigurationUUIDMessages}
                simpleValue
              />
              {data.BaseConfigurationUUID && (
                <Field
                  input={<Link />}
                  to={`/admin/report-configuration/${data.BaseConfigurationUUID}`}
                  openInNewTab={true}
                  content={<span>Open Base Configuration</span>}
                />
              )}
              <Field
                input={<ListDropdown />}
                label="Document Type"
                endpoint="list.reportableDocumentTypes"
                name="syncModel.DocumentId"
                value={data.DocumentId ? { label: data.DocumentName ?? '-', value: data.DocumentId } : null}
                visible={!data.IsSystemConfiguration}
                disabled={Boolean(data.BaseConfigurationUUID)}
                simpleValue
              />
              <Field
                input={<TextOutput />}
                label="Document Type"
                name="syncModel.DocumentName"
                visible={data.IsSystemConfiguration}
              />
              <Field
                label="Inclusion in Reports"
                input={<ListDropdown />}
                isMulti={true}
                name="syncModel.UsedInReports"
                endpoint="list.reportDocumentTypes"
                disabled={
                  data.IsSystemConfiguration ||
                  Boolean(data.BaseConfigurationUUID) ||
                  data.ConfigurationType !== 'EntityContent'
                }
              />
              <Field
                label="Groups"
                input={<ListDropdown />}
                isMulti={true}
                name="syncModel.Groups"
                endpoint="list.reportConfigGroups"
                disabled={data.IsSystemConfiguration}
              />
            </FieldSet>
          </ListFieldSection>
          <ListFieldSection wide>
            <Field
              input={<TextAreaInput />}
              label="XML Configuration"
              name="syncModel.Content"
              rows={20}
              readOnly={data.IsSystemConfiguration}
              messages={contentMessages}
              copyContentToClipboardLabel="Copy XML configuration content to clipboard"
            />
          </ListFieldSection>
        </SectionGroup>
      </PageSection>
    </>
  );
};

export default ReportConfigurationAdmin;
export { ReportConfigurationAdmin };
